import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { getFrequencyFromSeconds } from '@/hooks/api/use-create-schedule-mutation'
import {
  useScheduleTasksQuery,
  UseScheduleTasksSearchParams,
} from '@/hooks/api/use-schedule-tasks-query'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  type PaginationState,
  SortingState,
  type Updater,
  useReactTable,
} from '@tanstack/react-table'
import { T, useTranslate } from '@tolgee/react'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  XCircleIcon,
} from 'lucide-react'
import React from 'react'
import { ScheduleOptionsMenu } from '../_common/schedule-options-menu'
import { ScheduleAddDialog } from './_components/add-dialog'
import { ScheduleFilters } from './_components/schedule-filters'
import { frequencyOptions, RUN_STATUS_VALUES } from './_utils/constants'
import { FrequencyValue, RunStatus, ScheduleResponse } from './_utils/types'
import { PageTemplate } from '../_common/page-template'
import { Button } from '@/components/ui/button'
import { PlusIcon } from '@/components/icons/plus-icon'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { ScheduleWorkflows } from './_components/workflows'
import { DEFAULT_PAGINATION_OPTIONS, PaginationControls } from '@/components/ui/pagination-controls'

dayjs.extend(isToday)
dayjs.extend(isTomorrow)
dayjs.extend(localizedFormat)

type ScheduleTableDef = Omit<ScheduleResponse, 'frequency'> & {
  frequency: FrequencyValue
}

type ScheduleTableSortingState = {
  id: NonNullable<UseScheduleTasksSearchParams['sort']>
  desc: boolean
}[]

interface ScheduleTableProps {
  showAddButton?: boolean
}

function ScheduleTable({ showAddButton = true }: ScheduleTableProps) {
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGINATION_OPTIONS.defaultPageSize,
  })
  const [sorting, setSorting] = React.useState<ScheduleTableSortingState>([])
  const [filters, setFilters] = React.useState<ScheduleFilters>({})
  const [searchQuery, setSearchQuery] = React.useState<string>('')
  const [fromDate, setFromDate] = React.useState<Date>()
  const [toDate, setToDate] = React.useState<Date>()
  const [isAddScheduleDialogOpen, setIsAddScheduleDialogOpen] = React.useState(false)

  const scheduleTasksQuery = useScheduleTasksQuery({
    searchParams: {
      size: pagination.pageSize,
      page: pagination.pageIndex + 1,
      sort: sorting?.[0]?.id,
      order: sorting?.[0]?.desc ? 'desc' : 'asc',
      search_query: searchQuery || undefined,
      start_time_after: fromDate ? dayjs(fromDate).toISOString() : undefined,
      end_time_before: toDate ? dayjs(toDate).toISOString() : undefined,
      status: filters.status,
      last_run_status: filters.last_run_status,
      active: filters.active,
    },
  })

  const resetPageIndex = () => {
    setPagination((state) => ({
      ...state,
      pageIndex: 0,
    }))
  }

  const handleSortChange = (sort: Updater<SortingState>) => {
    setSorting(sort as ScheduleTableSortingState)
  }

  const handleAddButtonClick = () => {
    setIsAddScheduleDialogOpen(true)
  }

  function getDaySuffix(day: number): string {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  const getFrequencyLabel = (frequency: FrequencyValue): string => {
    const option = frequencyOptions.find((opt) => opt.value === frequency)
    return option?.label || 'Unknown'
  }

  const columnDefs = React.useMemo<ColumnDef<ScheduleTableDef>[]>(
    () => [
      {
        id: 'workflows',
        size: 20,
        cell: ({ row }) => {
          return (
            <TooltipIconButton
              keyName={'schedule.table.workflows.expand'}
              onClick={() => row.toggleExpanded()}
              className='text-neutral-150'
            >
              {row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </TooltipIconButton>
          )
        },
      },
      {
        accessorKey: 'agent_name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='schedule.table.headers.agent'>
            <T keyName='schedule.table.headers.agent' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return <p className='max-w-48 truncate font-medium'>{value}</p>
        },
      },
      {
        accessorKey: 'frequency',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='schedule.table.headers.frequency'>
            <T keyName='schedule.table.headers.frequency' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<FrequencyValue>()
          const label = getFrequencyLabel(value)
          return <span>{label}</span>
        },
      },
      {
        accessorKey: 'next_run',
        header: () => <T keyName='schedule.table.headers.nextRun' />,
        cell: ({ cell }) => {
          const value = cell.getValue<string | null>()

          if (!value) {
            return <span>No next run</span>
          }

          const date = dayjs(value)

          if (!date.isValid()) {
            return <span>Invalid date</span>
          }

          let label: string

          if (date.isToday()) {
            label = `Today at ${date.format('h:mm A')}`
          } else if (date.isTomorrow()) {
            label = `Tomorrow at ${date.format('h:mm A')}`
          } else {
            const day = date.date()
            const suffix = getDaySuffix(day)
            label = `On the ${day}${suffix} at ${date.format('h:mm A')}`
          }

          return <span>{label}</span>
        },
      },
      {
        accessorKey: 'last_run_status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='schedule.table.headers.lastRunStatus'>
            <T keyName='schedule.table.headers.lastRunStatus' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const status = cell.getValue<RunStatus | null>()
          return <RenderLastRunStatus status={status} />
        },
      },
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='schedule.table.headers.status'>
            <T keyName='schedule.table.headers.status' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => {
          const status = row.original.status
          const activityStatus = status === 'active' ? 'Active' : 'Paused'
          const dotColor = status === 'active' ? 'bg-success-200' : 'bg-grape-200'

          return (
            <span className='flex items-center gap-2'>
              <span className={`h-3 w-3 rounded-full ${dotColor}`} />
              <span className={status === 'paused' ? 'text-muted-foreground' : 'text-foreground'}>
                {activityStatus}
              </span>
            </span>
          )
        },
      },
      // {
      //   accessorKey: 'notifications',
      //   header: () => <T keyName='schedule.table.headers.notifications' />,
      //   cell: ({ cell }) => {
      //     const notifications = cell.getValue<{ email?: string } | null>()
      //     const hasEmail = Boolean(notifications?.email?.trim())

      //     return hasEmail ? (
      //       <T keyName='schedule.table.fields.email' />
      //     ) : (
      //       <T keyName='schedule.table.messages.noNotifications' />
      //     )
      //   },
      // },
      {
        id: 'actions',
        size: 20,
        cell: ({ row }) => (
          <div className='flex w-full items-center justify-start'>
            <ScheduleOptionsMenu
              id={row.original.id}
              agent_id={row.original.agent_id}
              tool_id={row.original.tool_id}
              status={row.original.status}
              startDate={row.original.start_time ? new Date(row.original.start_time) : new Date()}
              endDate={row.original.end_time ? new Date(row.original.end_time) : undefined}
              frequency={row.original.frequency}
              notifications={row.original.notifications}
              parameters={row.original.tool_args}
            />
          </div>
        ),
      },
    ],
    [],
  )

  const transformedData = React.useMemo(() => {
    if (!scheduleTasksQuery.data?.data) return []

    return scheduleTasksQuery.data.data.map(
      (item): ScheduleTableDef => ({
        ...item,
        frequency: getFrequencyFromSeconds(item.frequency),
      }),
    )
  }, [scheduleTasksQuery.data?.data])

  const table = useReactTable({
    data: transformedData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowCanExpand: () => true,
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: handleSortChange,
    sortDescFirst: true,
    state: {
      pagination,
      sorting,
    },
    manualPagination: true,
    manualSorting: true,
    enableFilters: false,
    pageCount: scheduleTasksQuery.data?.pagination.totalPages || 0,
  })

  const isRowPaused = (row: ScheduleTableDef) => {
    return row.status === 'paused'
  }
  const { rows } = table.getRowModel()
  const isTableLoading =
    scheduleTasksQuery.status === 'pending' || (scheduleTasksQuery.isFetching && rows.length === 0)

  return (
    <div className={cn('flex w-full flex-col', { 'gap-6': showAddButton })}>
      <PageTemplate.Header className='items-center justify-between'>
        <div>
          <PageTemplate.H4 className='font-normal text-neutral-500'>
            <T keyName='schedule.subtitle' />
          </PageTemplate.H4>
        </div>
        <Button onClick={handleAddButtonClick} className='shrink-0 gap-2'>
          <T keyName='schedule.addButton.title' />
          <PlusIcon className='size-4 text-mango-200' />
        </Button>
        {isAddScheduleDialogOpen && (
          <ScheduleAddDialog
            open={isAddScheduleDialogOpen}
            onOpenChange={setIsAddScheduleDialogOpen}
          />
        )}
      </PageTemplate.Header>

      <div className='w-full'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    data-state={
                      (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isTableLoading && <TableSkeletonRows cellCount={columnDefs.length} />}
            {scheduleTasksQuery.status === 'error' && <TableErrorRow colSpan={columnDefs.length} />}
            {scheduleTasksQuery.status === 'success' &&
              rows.length > 0 &&
              rows.map((row) => {
                const isPaused = isRowPaused(row.original)
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      key={row.id}
                      data-state={(row.getIsSelected() || row.getIsExpanded()) && 'selected'}
                      className={cn('h-[72px]')}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          data-state={
                            (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow data-state={row.getIsExpanded() && 'selected'}>
                        <TableCell colSpan={row.getVisibleCells().length} className='pb-0'>
                          <ScheduleWorkflows scheduleId={row.original.id} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}

            {scheduleTasksQuery.status === 'success' &&
              rows?.length === 0 &&
              !scheduleTasksQuery.isFetching && (
                <TableRow>
                  <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                    <T keyName='schedule.table.messages.noSchedules' />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        {scheduleTasksQuery.status === 'success' && (
          <div className='my-6'>
            <PaginationControls
              table={table}
              isTableLoading={isTableLoading}
              selectedPageIndex={pagination.pageIndex}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// Last Run Status Component
const LAST_RUN_STATUS: Record<
  RunStatus,
  { icon: React.ReactNode; name: string; color: 'error' | 'warning' | 'success' | 'cancelled' }
> = {
  [RUN_STATUS_VALUES.SUCCESS]: {
    icon: <CircleCheckIcon />,
    name: 'SUCCESS',
    color: 'success',
  },
  [RUN_STATUS_VALUES.FAILURE]: {
    icon: <CircleAlertIcon />,
    name: 'FAILURE',
    color: 'error',
  },
  [RUN_STATUS_VALUES.CANCELLED]: {
    icon: <XCircleIcon />,
    name: 'CANCELLED',
    color: 'cancelled',
  },
  [RUN_STATUS_VALUES.IN_PROGRESS]: {
    icon: <CircleDashedIcon />,
    name: 'IN PROGRESS',
    color: 'warning',
  },
  [RUN_STATUS_VALUES.PLANNING]: {
    icon: <CircleDashedIcon />,
    name: 'PLANNING',
    color: 'warning',
  },
}

export function RenderLastRunStatus({ status }: Readonly<{ status: RunStatus | null }>) {
  if (status === null) {
    return <span className='text-muted-foreground text-sm'>No last run</span>
  }

  const { name, color, icon } = LAST_RUN_STATUS[status]
  return (
    <div
      className={cn(
        'flex h-8 w-fit items-center gap-1.5 whitespace-nowrap rounded pl-2 pr-3 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0',
        color === 'success' && 'bg-success-100 text-success-300 [&>svg]:text-success-300',
        color === 'error' && 'bg-grape-100 text-grape-300 [&>svg]:text-grape-300',
        color === 'cancelled' && 'bg-gray-100 text-black/60 [&>svg]:text-black/60',
        color === 'warning' && 'bg-warning-100 text-warning-300 [&>svg]:text-warning-300',
      )}
    >
      {icon}
      <p className='text-xs font-normal'>{name}</p>
    </div>
  )
}

export { ScheduleTable }
